import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '../config/api';

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    fetchEventDetails();
  }, [eventId]);

  // Keyboard navigation for photo slider
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedPhoto || !event) return;
      
      if (e.key === 'ArrowLeft') {
        setCurrentPhotoIndex(prev => prev === 0 ? event.photos.length - 1 : prev - 1);
      } else if (e.key === 'ArrowRight') {
        setCurrentPhotoIndex(prev => prev === event.photos.length - 1 ? 0 : prev + 1);
      } else if (e.key === 'Escape') {
        setSelectedPhoto(null);
        setCurrentPhotoIndex(0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto, event]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/gallery/grouped');
      
      // Find the specific event by eventId
      const foundEvent = response.data.find(event => 
        event.eventName === eventId || 
        event.eventName.replace(/\s+/g, '-').toLowerCase() === eventId.toLowerCase()
      );
      
      if (foundEvent) {
        const eventData = {
          eventName: foundEvent.eventName,
          eventData: {
            _id: foundEvent.eventName,
            eventName: foundEvent.eventName,
            startDate: foundEvent.photos[0]?.eventDate || foundEvent.photos[0]?.createdAt,
            endDate: foundEvent.photos[0]?.eventDate || foundEvent.photos[0]?.createdAt
          },
          photos: foundEvent.photos
        };
        setEvent(eventData);
      } else {
        // Event not found, redirect to gallery
        navigate('/gallery');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      navigate('/gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (photo, photoIndex) => {
    setSelectedPhoto(photo);
    setCurrentPhotoIndex(photoIndex);
  };

  const handleCloseImage = () => {
    setSelectedPhoto(null);
    setCurrentPhotoIndex(0);
  };

  const handleNextImage = () => {
    if (event && event.photos) {
      const nextIndex = (currentPhotoIndex + 1) % event.photos.length;
      setCurrentPhotoIndex(nextIndex);
      setSelectedPhoto(event.photos[nextIndex]);
    }
  };

  const handlePrevImage = () => {
    if (event && event.photos) {
      const prevIndex = currentPhotoIndex === 0 ? event.photos.length - 1 : currentPhotoIndex - 1;
      setCurrentPhotoIndex(prevIndex);
      setSelectedPhoto(event.photos[prevIndex]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Event Not Found</h2>
          <button
            onClick={() => navigate('/gallery')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Gallery
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/gallery')}
            className="flex items-center text-white/90 hover:text-white mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Go Back
          </button>

          {/* Event Title and Description */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              {event.eventName}
            </h1>
            <p className="text-lg text-white/90 mb-6">
              All photos from {event.eventData.eventName}
            </p>
            
            {/* Event Stats */}
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{new Date(event.eventData.startDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{event.photos.length} photos</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Photos Grid Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {event.photos.map((photo, index) => (
            <div
              key={index}
              className="relative group cursor-pointer transform transition-all duration-300 hover:scale-105"
              onClick={() => handleImageClick(photo, index)}
            >
              <div className="relative overflow-hidden rounded-xl shadow-lg bg-white">
                <img
                  src={photo.image}
                  alt={photo.title || event.eventName}
                  loading="lazy"
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={e => {
                    e.target.src = '/images/heroSection_img.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Photo Slider Modal */}
      {selectedPhoto && event && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
          <div className="relative w-full h-full">
            {/* Close Button */}
            <button
              onClick={handleCloseImage}
              className="absolute top-4 right-4 z-20 text-white text-3xl font-bold bg-black/50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/70 transition-all backdrop-blur-sm"
            >
              ×
            </button>

            {/* Main Image */}
            <div className="relative h-full flex items-center justify-center p-4">
              <img
                src={event.photos[currentPhotoIndex].image}
                alt={event.photos[currentPhotoIndex].title || 'Gallery Photo'}
                className="max-w-full max-h-full object-contain rounded-lg"
                onError={e => {
                  e.target.src = '/images/heroSection_img.jpg';
                }}
              />
            </div>
            
            {/* Navigation Arrows */}
            {event.photos.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full w-12 h-12 flex items-center justify-center transition-all duration-200 z-10"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full w-12 h-12 flex items-center justify-center transition-all duration-200 z-10"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Photo Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm backdrop-blur-sm">
              {currentPhotoIndex + 1} / {event.photos.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailPage;
